import type { ApiGlossaryTerm } from '@/types/api'

export interface GlossaryGroup {
  key: string
  terms: ApiGlossaryTerm[]
}

// Gojuon row heads, in code point order. A kana sort value files under its row (か, ぎ and コ all
// under か), the way Japanese indexes group, rather than producing one group per kana.
const KANA_ROW_HEADS = 'あかさたなはまやらわ'

const KATAKANA_START = 0x30a1
const KATAKANA_END = 0x30f6
const HIRAGANA_START = 0x3041
const HIRAGANA_END = 0x3096
const KATAKANA_TO_HIRAGANA = 0x60

// Pinyin index letters (Hanyu Pinyin has no I, U or V initials) and, for each, a common character
// that sorts at or before every character with that initial under pinyin collation. A Han character
// files under the last boundary it does not precede.
const PINYIN_LETTERS = 'ABCDEFGHJKLMNOPQRSTWXYZ'
const PINYIN_BOUNDARIES = '阿八擦搭婀发嘎哈讥咔垃妈拿噢趴七然仨他挖夕丫匝'

const HAN = /\p{Script=Han}/u

let pinyinCollator: Intl.Collator | null = null

// Simplified Chinese indexes by pinyin; Traditional (zh-Hant, zh-TW, zh-HK) conventionally does not.
function usesPinyinIndex(language: string | undefined): boolean {
  if (!language) return false
  try {
    const locale = new Intl.Locale(language).maximize()
    return locale.language === 'zh' && locale.script === 'Hans'
  } catch {
    return false
  }
}

function pinyinInitial(char: string): string {
  pinyinCollator ??= new Intl.Collator('zh-u-co-pinyin')
  let letter = '#'
  for (let i = 0; i < PINYIN_BOUNDARIES.length; i++) {
    if (pinyinCollator.compare(char, PINYIN_BOUNDARIES[i]) >= 0) letter = PINYIN_LETTERS[i]
    else break
  }
  return letter
}

export function glossarySortKey(term: ApiGlossaryTerm): string {
  return term.sortValue?.trim() || term.term
}

/**
 * The jump-to group a sort key files under. Pinyin sort values group by their Latin initial; a
 * Chinese term without one groups by the pinyin initial of its first character when `language`
 * indexes by pinyin.
 */
export function glossaryGroupKey(sortKey: string, language?: string): string {
  // NFD splits voicing marks and Latin diacritics off the base character (が -> か, ǔ -> u, É -> E).
  const first = sortKey.trim().normalize('NFD')[0]
  if (!first) return '#'

  if (HAN.test(first) && usesPinyinIndex(language)) return pinyinInitial(first)

  let code = first.charCodeAt(0)
  if (code >= KATAKANA_START && code <= KATAKANA_END) code -= KATAKANA_TO_HIRAGANA
  if (code >= HIRAGANA_START && code <= HIRAGANA_END) {
    let head = KANA_ROW_HEADS[0]
    for (const candidate of KANA_ROW_HEADS) {
      if (candidate.charCodeAt(0) <= code) head = candidate
    }
    return head
  }

  return first.toUpperCase()
}

function createCollator(language: string | undefined): Intl.Collator {
  try {
    return new Intl.Collator(language)
  } catch {
    return new Intl.Collator()
  }
}

/** Sorts terms by sort value in the bundle language's collation and groups them by leading character. */
export function groupGlossaryTerms(terms: ApiGlossaryTerm[], language: string | undefined): GlossaryGroup[] {
  const collator = createCollator(language)
  const sorted = terms
    .map((term) => ({ term, sortKey: glossarySortKey(term) }))
    .sort((a, b) => collator.compare(a.sortKey, b.sortKey) || collator.compare(a.term.term, b.term.term))

  const groups = new Map<string, ApiGlossaryTerm[]>()
  for (const { term, sortKey } of sorted) {
    const key = glossaryGroupKey(sortKey, language)
    const group = groups.get(key)
    if (group) group.push(term)
    else groups.set(key, [term])
  }
  // Ordered by key rather than first appearance: a Chinese term still lacking a pinyin sort value
  // sorts after every Latin key yet may file under an earlier letter.
  return [...groups]
    .sort(([a], [b]) => (a === '#' ? 1 : b === '#' ? -1 : collator.compare(a, b)))
    .map(([key, groupTerms]) => ({ key, terms: groupTerms }))
}
